import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { ColDef } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import Button from '@/components/ui/Button'
import { FileText } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { formatDateForAPI } from '@/components/ui/DateRangePicker/utils'
import { TaskBudgetService } from '@/api/generated/services/TaskBudgetService'
import type { TaskBudgetResponse } from '@/api/generated/models/TaskBudgetResponse'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'

interface TaskBudgetsTableProps {
  workspaceId: string
  selectedBusinessId?: string
  selectedDepartmentId?: string
  selectedMemberId?: string
  isVisible: boolean
  startDate?: Date | null
  endDate?: Date | null
  searchQuery?: string
  searchTrigger?: number  // Explicit search trigger
  /** 'incomplete' | 'completed' */
  status?: string
  onBudgetItemClick?: (taskId: string) => void
  onReportClick?: (taskItem: TaskTreeNode) => void
  isActionButtonClicked?: boolean
  /** 값이 바뀌면 목록을 다시 조회한다 (수정/삭제 후 부모가 증가시킨다) */
  refreshTrigger?: number
}

const TaskBudgetsTable: React.FC<TaskBudgetsTableProps> = ({
  workspaceId,
  selectedBusinessId,
  selectedDepartmentId,
  selectedMemberId,
  isVisible,
  startDate,
  endDate,
  searchQuery,
  searchTrigger,
  status,
  onBudgetItemClick,
  onReportClick,
  isActionButtonClicked,
  refreshTrigger
}) => {
  // 인스턴스별 로컬 상태.
  // 전역 스토어를 쓰면 한 화면에 이 테이블이 둘 이상일 때(주간/월간 보고서의 당월·익월 목록)
  // 나중에 끝난 조회가 앞선 조회를 덮어써 두 목록이 같은 데이터를 보여준다.
  const [taskBudgets, setTaskBudgets] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set())
  // 마지막으로 시작한 조회의 번호. 늦게 도착한 이전 응답이 최신 결과를 덮어쓰지 않게 한다.
  const requestSeqRef = useRef(0)

  // Number formatting helper
  const formatCurrency = (value: string | number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return '-'
    return new Intl.NumberFormat('ko-KR').format(numValue) + '원'
  }

  // Percentage formatting helper
  const formatPercentage = (value: string | number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return '-'
    return numValue.toFixed(2) + '%'
  }

  // Row click handler
  const handleRowClick = useCallback((event: any) => {
    // Prevent row click if action button was clicked (TaskManagement pattern)
    if (isActionButtonClicked) return

    if (onBudgetItemClick && event.data && !event.data.isPlaceholder) {
      const budgetItem = event.data as TaskBudgetResponse
      if (budgetItem.task_id) {
        onBudgetItemClick(budgetItem.task_id)
      }
    }
  }, [onBudgetItemClick, isActionButtonClicked])

  // 목록 조회. reset이면 1페이지부터 새로 채우고, 아니면 해당 페이지 구간만 끼워 넣는다.
  const fetchItems = useCallback(async (page: number, reset: boolean = false) => {
    if (!workspaceId) return

    const seq = ++requestSeqRef.current

    try {
      setError(null)
      if (reset) {
        setIsLoading(true)
        setTaskBudgets([])
        setLoadedPages(new Set())
      } else {
        setIsLoadingMore(true)
      }

      const response = await TaskBudgetService.appsTaskViewsListTaskBudgets({
        workspaceId,
        businessId: selectedBusinessId || undefined,
        departmentId: selectedDepartmentId || undefined,
        stakeholderId: selectedMemberId || undefined,
        startDate: startDate ? formatDateForAPI(startDate) : undefined,
        endDate: endDate ? formatDateForAPI(endDate) : undefined,
        q: searchQuery || undefined,
        status: status || undefined,
        page,
        perPage: pageSize,
      })

      // 이 응답이 도착하기 전에 더 최신 조회가 시작됐다면 결과를 버린다
      if (seq !== requestSeqRef.current) return

      const newItems = response?.data ?? []
      const total = response?.meta?.total ?? 0

      if (reset) {
        // 전체 길이만큼 자리를 만들고 1페이지 구간만 실제 데이터로 채운다 (스파스 배열)
        const sparse: any[] = []
        for (let i = 0; i < total; i++) {
          sparse.push(i < newItems.length
            ? newItems[i]
            : { id: `placeholder-task-budget-${i}`, isPlaceholder: true })
        }
        setTaskBudgets(sparse)
      } else {
        const startIndex = (page - 1) * pageSize
        setTaskBudgets(prev => {
          const updated = [...prev]
          while (updated.length < total) {
            updated.push({ id: `placeholder-task-budget-${updated.length}`, isPlaceholder: true })
          }
          for (let i = 0; i < newItems.length; i++) {
            updated[startIndex + i] = newItems[i]
          }
          return updated
        })
      }

      setTotalItems(total)
      setLoadedPages(prev => new Set([...prev, page]))
    } catch (err) {
      if (seq !== requestSeqRef.current) return
      console.error('Failed to fetch taskBudgets:', err)
      setError('예산 목록을 불러오는데 실패했습니다.')
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    }
  }, [workspaceId, selectedBusinessId, selectedDepartmentId, selectedMemberId, startDate, endDate, searchQuery, status, pageSize])

  // 필터/검색/기간이 바뀌면 처음부터 다시 조회
  useEffect(() => {
    if (isVisible && workspaceId) {
      fetchItems(1, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, workspaceId, selectedBusinessId, selectedDepartmentId, selectedMemberId, startDate, endDate, searchQuery, searchTrigger, status, pageSize, refreshTrigger])

  // 페이지 전환 — 아직 안 받은 구간이면 그 페이지만 채워 넣는다
  const handlePaginationChanged = useCallback((params: any) => {
    const api = params.api
    if (!api || !workspaceId) return

    const currentPageNum = api.paginationGetCurrentPage() + 1
    const currentPageSize = api.paginationGetPageSize()

    if (currentPageSize !== pageSize) {
      setPageSize(currentPageSize)   // pageSize 변경은 위 useEffect가 재조회를 트리거한다
      return
    }

    if (isLoading || isLoadingMore || loadedPages.has(currentPageNum)) return

    const startIndex = (currentPageNum - 1) * pageSize
    const actualEndIndex = Math.min(startIndex + pageSize, totalItems)

    let needsLoad = actualEndIndex > taskBudgets.length
    if (!needsLoad) {
      for (let i = startIndex; i < actualEndIndex; i++) {
        if (!taskBudgets[i] || taskBudgets[i].isPlaceholder) {
          needsLoad = true
          break
        }
      }
    }

    if (needsLoad) {
      fetchItems(currentPageNum, false)
    }
  }, [workspaceId, pageSize, taskBudgets, totalItems, isLoading, isLoadingMore, loadedPages, fetchItems])

  // Convert TaskBudgetResponse to TaskTreeNode format for ReportDetailPanel
  const convertBudgetToTaskTreeNode = useCallback((budget: TaskBudgetResponse): TaskTreeNode => {
    return {
      id: budget.task_id,
      type: 'task',
      name: budget.task_name || '',
      business_name: '', // Will be populated from budget data if available
      department_name: '',
      assignee: undefined,
      sub_assignee: undefined,
      manager: undefined,
      reviewer: undefined,
      orgHierarchy: [],
      originalData: budget
    }
  }, [])


  // Column definitions with specified fields
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '상태',
      field: 'status',
      width: 100,
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded w-16"></div>
        }
        if (!params.value) return '-'

        const getStatusColor = (status: string) => {
          switch (status) {
            case 'completed':
              return 'success'
            case 'reviewed':
              return 'info'
            case 'assigned':
              return 'warning'
            case 'incomplete':
            default:
              return 'gray'
          }
        }

        const getStatusText = (status: string) => {
          switch (status) {
            case 'completed':
              return '완료'
            case 'reviewed':
              return '검토완료'
            case 'assigned':
              return '등록'
            case 'incomplete':
            default:
              return '미완료'
          }
        }

        return (
          <div className="flex items-center h-full">
            <Tag color={getStatusColor(params.value)} size="small">
              {getStatusText(params.value)}
            </Tag>
          </div>
        )
      }
    },
    {
      headerName: '세부실행과제명',
      field: 'task_name',
      flex: 2,
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        // Placeholder row handling
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[6],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '액션',
      field: 'actions',
      width: 100,
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded w-16"></div>
        }

        const handleBudgetReportClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          if (onReportClick && params.data) {
            const budgetItem = params.data as TaskBudgetResponse
            const treeNode = convertBudgetToTaskTreeNode(budgetItem)
            onReportClick(treeNode)
          }
        }

        return (
          <div className="flex items-center justify-center h-full">
            <Button size="small" color="light" onClick={handleBudgetReportClick}>
              <FileText className="w-3 h-3" />
              보고서
            </Button>
          </div>
        )
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      } as any
    },
    {
      headerName: '목표 예산',
      field: 'items_target_figure',
      width: 150,
      minWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        // 표시할 때는 포맷팅 적용
        return formatCurrency(params.value || '0')
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '집행 예산',
      field: 'items_done_figure',
      width: 150,
      minWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        // 표시할 때는 포맷팅 적용
        return formatCurrency(params.value || '0')
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    },
    {
      headerName: '집행율',
      field: 'items_figure_percentage',
      width: 100,
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        // 표시할 때는 포맷팅 적용
        return formatPercentage(params.value || '0')
      },
      cellStyle: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: designTokens.typography.fontSize['body-small'][0],
        color: designTokens.colors.gray[5],
        display: 'flex',
        alignItems: 'center'
      } as any
    }
  ], [])

  // 조회 실패 안내. 이미 받아 둔 목록이 있으면(페이지 조회만 실패한 경우) 그리드를 유지한다.
  if (error && taskBudgets.length === 0) {
    return (
      <div
        className="flex items-center justify-center h-64"
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color: designTokens.colors.error.DEFAULT
        }}
      >
        {error}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <DataGrid
        columnDefs={columnDefs}
        rowData={taskBudgets}
        rowModelType="clientSide"
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={[20, 50, 100]}

        // Server total count display
        localeText={{
          ...AG_GRID_LOCALE_KR,
          paginationOf: `총 ${totalItems.toLocaleString()}개`,
          page: '페이지',
          to: '-',
          of: `중`
        }}

        // Pagination event handler
        onPaginationChanged={handlePaginationChanged}

        // Row ID configuration
        getRowId={(params: any) => {
          if (!params.data) return null
          return params.data.id
        }}

        onRowClicked={handleRowClick}
        loading={isLoading || isLoadingMore}
        headerHeight={40}
        rowHeight={42}
        height={40 + (20 * 42) + 60 + 2} // header + (10 rows * rowHeight) + pagination height + border
        suppressMovableColumns={true}
        suppressMenuHide={true}

        // 범위 선택 기능 활성화
        enableRangeSelection={true}
        rangeSelectionExcludeColumns={['actions']}
        enableCellCopy={true}
        suppressRowClickSelection={true}
        rangeCopyFormatter={(colId, value) => {
          // 상태 컬럼 포맷팅
          if (colId === 'status') {
            switch (value) {
              case 'completed': return '완료'
              case 'reviewed': return '검토완료'
              case 'assigned': return '등록'
              case 'incomplete':
              default: return '미완료'
            }
          }
          // 금액/퍼센트 컬럼은 원본 값 그대로 복사 (엑셀에서 숫자로 인식되도록)
          if (colId === 'items_target_figure' || colId === 'items_done_figure' || colId === 'items_figure_percentage') {
            if (value === null || value === undefined) return ''
            return value.toString()
          }
          // null/undefined 처리
          if (value === null || value === undefined) return '-'
          return value.toString()
        }}
      />
    </div>
  )
}

export default TaskBudgetsTable